import { useState } from 'react';
import { getInitials } from '@/lib/platformLogos';

/**
 * PlatformLogo - Shows a company logo via favicon, falling back to styled initials.
 * Priority: 1) Google Favicon API  2) DB logo_text_initials  3) Auto-generated initials
 */

function getFaviconUrl(website, highRes = false) {
  if (!website) return null;
  try {
    const domain = new URL(website).hostname;
    const sz = highRes ? 128 : 64;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=${sz}`;
  } catch {
    return null;
  }
}

export default function PlatformLogo({ website, name, logoInitials, logoColor, size = 'md', className = '' }) {
  const [imgError, setImgError] = useState(false);
  const highRes = size === 'xl' || size === 'lg';
  const faviconUrl = getFaviconUrl(website, highRes);

  const sizeMap = {
    sm: { container: 'w-6 h-6', img: 'w-4 h-4', text: 'text-[9px]' },
    md: { container: 'w-8 h-8', img: 'w-5 h-5', text: 'text-[10px]' },
    lg: { container: 'w-10 h-10', img: 'w-6 h-6', text: 'text-[11px]' },
    xl: { container: 'w-14 h-14', img: 'w-9 h-9', text: 'text-sm' },
  };
  const s = sizeMap[size] || sizeMap.md;

  // Try favicon first, fall back to initials on error
  if (faviconUrl && !imgError) {
    return (
      <div
        className={`${s.container} rounded-lg bg-white border border-slate-200/80 flex items-center justify-center shrink-0 overflow-hidden ${className}`}
        data-testid="platform-logo"
        title={name}
      >
        <img
          src={faviconUrl}
          alt={`${name} logo`}
          className={`${s.img} object-contain`}
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  }

  // Fallback: styled initials
  const initials = logoInitials || getInitials(name);
  const bgStyle = logoColor
    ? { backgroundColor: logoColor, color: '#fff' }
    : { backgroundColor: '#94a3b8', color: '#fff' };

  return (
    <div
      className={`${s.container} rounded-lg flex items-center justify-center font-bold ${s.text} shrink-0 ${className}`}
      style={bgStyle}
      data-testid="platform-logo-fallback"
      title={name}
      role="img"
      aria-label={`${name} logo`}
    >
      {initials}
    </div>
  );
}
